/* eslint-disable no-plusplus */
/* eslint-disable no-continue */
/* eslint-disable no-await-in-loop */
import React, { useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  Flex,
  Badge,
  Divider,
  Spinner,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Button,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, TooltipProps } from 'recharts';
import moment from 'moment';
import 'moment/locale/pt-br';
import { Link, createSearchParams } from 'react-router-dom';
import { api } from '../../../../../services/api';
import { IPartner } from '../../../../../hooks/useCompanies';
import { toCurrency, formatCPF, formatCNPJ } from '../../../../../utils/FormatFunctions';

moment.locale('pt-br');

interface PartnerDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  partner: IPartner | null;
  currentYear: number;
}

const formatCPForCNPJ = (value: string | number | null | undefined): string => {
  if (!value) return '';
  const cleanValue = value.toString().replace(/\D/g, '');

  // CNPJ tem 14 dígitos, CPF tem 11 dígitos
  if (cleanValue.length === 14) {
    return formatCNPJ(value);
  }
  return formatCPF(value);
};

// Componente customizado de Tooltip que evita sobreposição com o centro do gráfico
const CustomTooltip = ({ active, payload }: TooltipProps<number, string>) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  const empresa = data?.empresa || 'Empresa';
  const valor = payload[0].value || 0;

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      boxShadow="lg"
      p={3}
      maxW="250px"
      zIndex={1000}
    >
      <Text fontSize="sm" fontWeight="bold" color="gray.800" mb={1} noOfLines={2}>
        {empresa}
      </Text>
      <Text fontSize="xs" color="gray.600" fontWeight="semibold">
        {toCurrency(valor)}
      </Text>
    </Box>
  );
};

export function PartnerDetailModal({
  isOpen,
  onClose,
  partner,
  currentYear,
}: PartnerDetailModalProps) {
  // Buscar empresas e faturamentos para Simples Nacional
  const { data: companiesDataSimples, isFetching: isFetchingSimples } = useQuery({
    queryKey: ['partnerCompaniesSimples', partner?.socio_id, currentYear],
    queryFn: async () => {
      if (!partner?.socio_id) return [];

      try {
        const dateStart = moment().year(currentYear).startOf('year').format('YYYY-MM-DD');
        const dateEnd = moment().year(currentYear).endOf('year').format('YYYY-MM-DD');

        // eslint-disable-next-line camelcase
        const res = await api.get('/api/v1/regime_tributario/limite/', {
          params: {
            socio_id: partner.socio_id,
            regime_tributario: 'simples_nacional',
            date_start: dateStart,
            // eslint-disable-next-line camelcase
            date_end: dateEnd,
          },
        });

        const items = res.data?.data?.items || [];
        return items.map((item: any) => ({
          empresa: item.razao_social || item.nome || 'Sem nome',
          cnpj: item.cnpj || '',
          faturamento_acumulado: item.faturamento_acumulado || 0,
        }));
      } catch {
        return [];
      }
    },
    enabled: isOpen && !!partner?.socio_id,
    staleTime: 1000 * 60 * 5,
  });

  // Buscar empresas e faturamentos para Lucro Presumido
  const { data: companiesDataPresumido, isFetching: isFetchingPresumido } = useQuery({
    queryKey: ['partnerCompaniesPresumido', partner?.socio_id, currentYear],
    queryFn: async () => {
      if (!partner?.socio_id) return [];

      try {
        const dateStart = moment().year(currentYear).startOf('year').format('YYYY-MM-DD');
        const dateEnd = moment().year(currentYear).endOf('year').format('YYYY-MM-DD');

        // eslint-disable-next-line camelcase
        const res = await api.get('/api/v1/regime_tributario/limite/', {
          params: {
            socio_id: partner.socio_id,
            regime_tributario: 'lucro_presumido',
            date_start: dateStart,
            // eslint-disable-next-line camelcase
            date_end: dateEnd,
          },
        });

        const items = res.data?.data?.items || [];
        return items.map((item: any) => ({
          empresa: item.razao_social || item.nome || 'Sem nome',
          cnpj: item.cnpj || '',
          faturamento_acumulado: item.faturamento_acumulado || 0,
        }));
      } catch {
        return [];
      }
    },
    enabled: isOpen && !!partner?.socio_id,
    staleTime: 1000 * 60 * 5,
  });

  const chartDataSimples = useMemo(() => {
    if (!companiesDataSimples || companiesDataSimples.length === 0) return [];
    return companiesDataSimples.sort((a, b) => b.faturamento_acumulado - a.faturamento_acumulado);
  }, [companiesDataSimples]);

  const chartDataPresumido = useMemo(() => {
    if (!companiesDataPresumido || companiesDataPresumido.length === 0) return [];
    return companiesDataPresumido.sort((a, b) => b.faturamento_acumulado - a.faturamento_acumulado);
  }, [companiesDataPresumido]);

  // Calcular totais de empresas
  const totalEmpresasSimples = companiesDataSimples?.length || 0;
  const totalEmpresasPresumido = companiesDataPresumido?.length || 0;

  const totalEmpresas = totalEmpresasSimples + totalEmpresasPresumido;

  if (!partner) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={2}>
          <Flex align="center" gap={3}>
            <Box>
              <Text fontSize="lg" fontWeight="bold">
                {partner.nome_socio}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {formatCPForCNPJ(partner.inscricao)} | ID: {partner.socio_id}
              </Text>
            </Box>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          <Flex align="center" gap={2} mb={4}>
            <Badge colorScheme="blue" fontSize="sm" px={2} py={1}>
              Ano: {currentYear}
            </Badge>
          </Flex>

          <Divider mb={6} />

          {/* Resumo de Empresas */}
          <SimpleGrid columns={3} spacing={4} mb={6}>
            <Stat>
              <StatLabel fontSize="xs" color="gray.500">
                Total de Empresas
              </StatLabel>
              <StatNumber fontSize="lg" color="blue.600">
                {totalEmpresas}
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel fontSize="xs" color="gray.500">
                Simples Nacional
              </StatLabel>
              <StatNumber fontSize="lg" color="yellow.600">
                {totalEmpresasSimples}
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel fontSize="xs" color="gray.500">
                Lucro Presumido
              </StatLabel>
              <StatNumber fontSize="lg" color="teal.600">
                {totalEmpresasPresumido}
              </StatNumber>
            </Stat>
          </SimpleGrid>

          <Divider mb={6} />

          {/* Gráfico Simples Nacional */}
          {totalEmpresasSimples > 0 && (
            <Box bg="yellow.50" p={4} borderRadius="lg" mb={6}>
              <Text fontSize="md" fontWeight="bold" mb={3} color="yellow.800">
                Simples Nacional - Empresas e Faturamento
              </Text>

              {isFetchingSimples ? (
                <Flex justify="center" align="center" h="350px">
                  <Spinner size="lg" color="yellow.600" />
                </Flex>
              ) : (
                <Box position="relative" w="100%" h="350px">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartDataSimples}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={false}
                        outerRadius={120}
                        innerRadius={60}
                        fill="#8884d8"
                        dataKey="faturamento_acumulado"
                      >
                        {chartDataSimples.map((entry, index) => {
                          const colors = [
                            '#F6AD55',
                            '#ED8936',
                            '#DD6B20',
                            '#C05621',
                            '#9C4221',
                            '#7C2D12',
                            '#FBBF24',
                            '#F59E0B',
                            '#D97706',
                            '#B45309',
                          ];
                          return (
                            <Cell
                              key={`cell-simples-${entry.cnpj || entry.empresa || String(index)}`}
                              fill={colors[index % colors.length]}
                            />
                          );
                        })}
                      </Pie>
                      <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ fill: 'transparent' }}
                        position={{ x: 0, y: 0 }}
                        allowEscapeViewBox={{ x: true, y: true }}
                        offset={20}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <Flex
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    direction="column"
                    align="center"
                    justify="center"
                    zIndex={100}
                    pointerEvents="none"
                    bg="white"
                    borderRadius="full"
                    w="120px"
                    h="120px"
                    boxShadow="sm"
                  >
                    <Text fontSize="2xl" fontWeight="bold" color="yellow.700">
                      {totalEmpresasSimples}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Empresas
                    </Text>
                    <Text fontSize="10px" color="gray.500" mt={1}>
                      {toCurrency(
                        chartDataSimples.reduce((acc, item) => acc + item.faturamento_acumulado, 0),
                      )}
                    </Text>
                  </Flex>
                </Box>
              )}
            </Box>
          )}

          {/* Gráfico Lucro Presumido */}
          {totalEmpresasPresumido > 0 && (
            <Box bg="teal.50" p={4} borderRadius="lg">
              <Text fontSize="md" fontWeight="bold" mb={3} color="teal.800">
                Lucro Presumido - Empresas e Faturamento
              </Text>

              {isFetchingPresumido ? (
                <Flex justify="center" align="center" h="350px">
                  <Spinner size="lg" color="teal.600" />
                </Flex>
              ) : (
                <Box position="relative" w="100%" h="350px">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartDataPresumido}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={false}
                        outerRadius={120}
                        innerRadius={60}
                        fill="#8884d8"
                        dataKey="faturamento_acumulado"
                      >
                        {chartDataPresumido.map((entry, index) => {
                          const colors = [
                            '#4FD1C7',
                            '#38B2AC',
                            '#319795',
                            '#2C7A7B',
                            '#285E61',
                            '#234E52',
                            '#5EEAD4',
                            '#2DD4BF',
                            '#14B8A6',
                            '#0D9488',
                          ];
                          return (
                            <Cell
                              key={`cell-presumido-${entry.cnpj || entry.empresa || String(index)}`}
                              fill={colors[index % colors.length]}
                            />
                          );
                        })}
                      </Pie>
                      <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ fill: 'transparent' }}
                        position={{ x: 0, y: 0 }}
                        allowEscapeViewBox={{ x: true, y: true }}
                        offset={20}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <Flex
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    direction="column"
                    align="center"
                    justify="center"
                    zIndex={100}
                    pointerEvents="none"
                    bg="white"
                    borderRadius="full"
                    w="120px"
                    h="120px"
                    boxShadow="sm"
                  >
                    <Text fontSize="2xl" fontWeight="bold" color="teal.700">
                      {totalEmpresasPresumido}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Empresas
                    </Text>
                    <Text fontSize="10px" color="gray.500" mt={1}>
                      {toCurrency(
                        chartDataPresumido.reduce(
                          (acc, item) => acc + item.faturamento_acumulado,
                          0,
                        ),
                      )}
                    </Text>
                  </Flex>
                </Box>
              )}
            </Box>
          )}
        </ModalBody>

        <ModalFooter>
          <Link
            to={{
              pathname: `/modulesHome/supervisor/partner/${partner.socio_id}`,
              search: createSearchParams({
                date_start: moment().year(currentYear).startOf('year').format('YYYY-MM-DD'),
                date_end: moment().year(currentYear).endOf('year').format('YYYY-MM-DD'),
              }).toString(),
            }}
            state={{ company: partner }}
          >
            <Button colorScheme="blue" variant="outline" mr={3}>
              Ver empresas
            </Button>
          </Link>
          <Button onClick={onClose}>Fechar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
